import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";

// Local imports
import { stationMetaData } from "../../../../utils/rawData";
import { getCommunityById } from "../../../../utils/communityData";
import {
  getKioskConfig,
  postKioskConfig,
  patchKioskConfig,
  uploadKioskImage,
  updateKioskImage,
} from "../../../../services/api";

// Utility imports
import {
  DEFAULT_TIMER,
  SNACKBAR_AUTO_HIDE_DURATION,
} from "../../utils/constants";
import {
  getUnique,
  safeToString,
  filterRows,
  isValidMapConfig,
} from "../../utils/helpers";

/**
 * Custom hook for managing kiosk settings state and logic
 *
 * @returns {Object} Hook state and handlers
 */
export default function useKioskSettings() {
  // Route parameters
  const { communityId } = useParams();
  const community = getCommunityById(communityId);

  // State management
  const [rows, setRows] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    type: "",
    Community: "",
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [configExists, setConfigExists] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [mapConfig, setMapConfig] = useState({
    center: null,
    zoom: null,
    bounds: null,
  });
  const [mapLanguages, setMapLanguages] = useState({
    english: true,
    inuktitut: true,
  });
  const [showWindBarbMap, setShowWindBarbMap] = useState(false);
  const [windBarbMapLanguages, setWindBarbMapLanguages] = useState({
    english: true,
    inuktitut: true,
  });
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [lastModified, setLastModified] = useState(null);
  const [selectedStations, setSelectedStations] = useState([]);
  const [draggedStation, setDraggedStation] = useState(null);
  const [announcementPhotoUrl, setAnnouncementPhotoUrl] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [introImages, setIntroImages] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize rows from station metadata
  useEffect(() => {
    const allStations = stationMetaData;
    setRows(
      allStations.map((station) => ({
        ...station,
        display: false,
        timer: "",
        lastTimer: DEFAULT_TIMER,
        order: 0,
        languages: {
          english: false,
          inuktitut: false,
        },
      }))
    );
  }, []);

  // Fetch existing configuration on component mount
  useEffect(() => {
    if (!community) return;

    const fetchConfig = async () => {
      setLoading(true);
      setError("");
      setNotConfigured(false);

      try {
        const data = await getKioskConfig(communityId);

        if (data && data.config && Array.isArray(data.config.stations)) {
          // Update rows with existing configuration
          setRows((prevRows) =>
            prevRows.map((row) => {
              const found = data.config.stations.find((s) => s.id === row.id);
              return found
                ? {
                    ...row,
                    display: true,
                    timer: found.timer?.toString() || DEFAULT_TIMER,
                    lastTimer: found.timer?.toString() || DEFAULT_TIMER,
                    order: found.order || 0,
                    languages: found.languages || {
                      english: true,
                      inuktitut: true,
                    },
                  }
                : {
                    ...row,
                    display: false,
                    timer: "",
                    lastTimer: DEFAULT_TIMER,
                    order: 0,
                    languages: { english: false, inuktitut: false },
                  };
            })
          );

          // Update selected stations with order
          const selectedWithOrder = data.config.stations
            .map((station, index) => ({
              ...station,
              order: station.order || index + 1,
              languages: station.languages || {
                english: true,
                inuktitut: true,
              },
            }))
            .sort((a, b) => a.order - b.order);
          setSelectedStations(selectedWithOrder);

          // Load map configuration if it exists
          if (data.config.map) {
            const mapConfig = data.config.map;
            try {
              setMapConfig({
                center: mapConfig.center
                  ? [
                      parseFloat(mapConfig.center[0]) || 0,
                      parseFloat(mapConfig.center[1]) || 0,
                    ]
                  : null,
                zoom: mapConfig.zoom ? parseFloat(mapConfig.zoom) || 5.2 : null,
                bounds: mapConfig.bounds
                  ? [
                      [
                        parseFloat(mapConfig.bounds[0][0]) || 0,
                        parseFloat(mapConfig.bounds[0][1]) || 0,
                      ],
                      [
                        parseFloat(mapConfig.bounds[1][0]) || 0,
                        parseFloat(mapConfig.bounds[1][1]) || 0,
                      ],
                    ]
                  : null,
              });

              // Load map languages if they exist
              if (mapConfig.languages) {
                setMapLanguages(mapConfig.languages);
              } else {
                setMapLanguages({ english: true, inuktitut: true });
              }
            } catch (error) {
              console.warn("Error parsing map configuration:", error);
              setMapConfig({
                center: null,
                zoom: null,
                bounds: null,
              });
              setMapLanguages({ english: true, inuktitut: true });
            }
          }

          // Load wind barb map configuration if it exists
          if (data.config.windBarbMap) {
            const windBarbMapConfig = data.config.windBarbMap;
            setShowWindBarbMap(windBarbMapConfig.enabled || false);
            if (windBarbMapConfig.languages) {
              setWindBarbMapLanguages(windBarbMapConfig.languages);
            } else {
              setWindBarbMapLanguages({ english: true, inuktitut: true });
            }
          }

          // Load announcement photo if it exists
          if (data.imageUrl) {
            setAnnouncementPhotoUrl(data.imageUrl);
            setPendingImageFile(null);
          }

          // Load intro images if they exist
          if (data.introImages) {
            setIntroImages(data.introImages);
          } else {
            setIntroImages([]);
          }

          // Store lastModified information if available
          if (data.lastModified) {
            setLastModified(data.lastModified);
          }

          setConfigExists(true);
        }
      } catch (err) {
        // Handle 404 (not configured yet) gracefully
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "";
        const status = err?.response?.status;

        if (
          (typeof msg === "string" &&
            msg.toLowerCase().includes("not found")) ||
          status === 404
        ) {
          setConfigExists(false);
          setNotConfigured(true);
        } else {
          setError(msg || "Failed to load config");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [communityId, community]);

  // Generate filter options from station data
  const statusOptions = getUnique(stationMetaData, "status");
  const typeOptions = getUnique(stationMetaData, "type");
  const communityOptions = getUnique(stationMetaData, "Community");

  // Filter rows based on search and filter criteria
  const filteredRows = useMemo(() => {
    return filterRows(rows, filters, search);
  }, [rows, filters, search]);

  // Event handlers
  const handleDisplayChange = (index) => (event) => {
    const updatedRows = [...rows];
    const checked = event.target.checked;

    if (checked) {
      updatedRows[index].display = true;
      updatedRows[index].timer = updatedRows[index].lastTimer || DEFAULT_TIMER;
      // Set default languages when station is selected
      updatedRows[index].languages = {
        english: true,
        inuktitut: true,
      };
      const nextOrder = selectedStations.length + 1;
      updatedRows[index].order = nextOrder;
      setSelectedStations([
        ...selectedStations,
        { ...updatedRows[index], order: nextOrder },
      ]);
    } else {
      updatedRows[index].display = false;
      updatedRows[index].lastTimer = updatedRows[index].timer || DEFAULT_TIMER;
      updatedRows[index].timer = "";
      updatedRows[index].order = 0;
      // Reset languages when station is deselected
      updatedRows[index].languages = {
        english: false,
        inuktitut: false,
      };
      const remainingStations = selectedStations.filter(
        (station) => station.id !== updatedRows[index].id
      );

      const reorderedStations = remainingStations.map((station, idx) => ({
        ...station,
        order: idx + 1,
      }));

      setSelectedStations(reorderedStations);

      reorderedStations.forEach((station) => {
        const rowIndex = updatedRows.findIndex((row) => row.id === station.id);
        if (rowIndex !== -1) {
          updatedRows[rowIndex].order = station.order;
        }
      });
    }

    setRows(updatedRows);
    setHasUnsavedChanges(true);
  };

  const handleLanguageChange = (index) => (language) => (event) => {
    const updatedRows = [...rows];
    const checked = event.target.checked;
    const currentLanguages = updatedRows[index].languages || {
      english: false,
      inuktitut: false,
    };

    // If trying to uncheck a language, ensure at least one language remains selected
    if (!checked) {
      const otherLanguage = language === "english" ? "inuktitut" : "english";
      if (!currentLanguages[otherLanguage]) {
        // Don't allow deselecting if it would leave no languages selected
        return;
      }
    }

    updatedRows[index].languages = {
      ...currentLanguages,
      [language]: checked,
    };

    // Update selectedStations as well
    setSelectedStations((prev) =>
      prev.map((station) =>
        station.id === updatedRows[index].id
          ? { ...station, languages: updatedRows[index].languages }
          : station
      )
    );

    setRows(updatedRows);
    setHasUnsavedChanges(true);
  };

  const handleTimerChange = (index) => (event) => {
    const updatedRows = [...rows];
    const value = event.target.value.replace(/[^0-9]/g, "");
    updatedRows[index].timer = value;
    updatedRows[index].lastTimer = value || DEFAULT_TIMER;
    setRows(updatedRows);

    const stationId = updatedRows[index].id;
    const stationInSelected = selectedStations.find((s) => s.id === stationId);
    if (stationInSelected) {
      setSelectedStations((prevSelected) =>
        prevSelected.map((station) =>
          station.id === stationId
            ? { ...station, timer: value, lastTimer: value || DEFAULT_TIMER }
            : station
        )
      );
    }
    setHasUnsavedChanges(true);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleMapLanguageChange = (newMapLanguages) => {
    setMapLanguages(newMapLanguages);
    setHasUnsavedChanges(true);
  };

  const handleMapDialogOpen = () => {
    setMapDialogOpen(true);
  };

  const handleMapDialogClose = () => {
    setMapDialogOpen(false);
  };

  const handleMapAreaSelect = (mapArea) => {
    setMapConfig({
      center: mapArea.center,
      zoom: mapArea.zoom,
      bounds: mapArea.bounds,
    });
    setMapDialogOpen(false);
    setHasUnsavedChanges(true);
  };

  const handleDeleteMap = () => {
    setMapConfig({
      center: null,
      zoom: null,
      bounds: null,
    });
    // Also disable wind barb map when deleting map configuration
    setShowWindBarbMap(false);
    setHasUnsavedChanges(true);
  };

  const handleWindBarbMapToggle = (enabled) => {
    setShowWindBarbMap(enabled);
    setHasUnsavedChanges(true);
  };

  const handleWindBarbMapLanguageChange = (newWindBarbMapLanguages) => {
    setWindBarbMapLanguages(newWindBarbMapLanguages);
    setHasUnsavedChanges(true);
  };

  const moveStationUp = (index) => {
    if (index === 0) return;
    const updatedStations = [...selectedStations];
    const temp = updatedStations[index];
    updatedStations[index] = updatedStations[index - 1];
    updatedStations[index - 1] = temp;

    updatedStations.forEach((station, idx) => {
      station.order = idx + 1;
    });

    setSelectedStations(updatedStations);

    setRows((prevRows) =>
      prevRows.map((row) => {
        const updatedStation = updatedStations.find((s) => s.id === row.id);
        return updatedStation ? { ...row, order: updatedStation.order } : row;
      })
    );
    setHasUnsavedChanges(true);
  };

  const moveStationDown = (index) => {
    if (index === selectedStations.length - 1) return;
    const updatedStations = [...selectedStations];
    const temp = updatedStations[index];
    updatedStations[index] = updatedStations[index + 1];
    updatedStations[index + 1] = temp;

    updatedStations.forEach((station, idx) => {
      station.order = idx + 1;
    });

    setSelectedStations(updatedStations);

    setRows((prevRows) =>
      prevRows.map((row) => {
        const updatedStation = updatedStations.find((s) => s.id === row.id);
        return updatedStation ? { ...row, order: updatedStation.order } : row;
      })
    );
    setHasUnsavedChanges(true);
  };

  const handleDragStart = (index, event) => {
    setDraggedStation(index);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (dropIndex, event) => {
    event.preventDefault();

    if (draggedStation === null || draggedStation === dropIndex) {
      setDraggedStation(null);
      return;
    }

    const updatedStations = [...selectedStations];
    const draggedItem = updatedStations[draggedStation];

    updatedStations.splice(draggedStation, 1);
    updatedStations.splice(dropIndex, 0, draggedItem);

    updatedStations.forEach((station, idx) => {
      station.order = idx + 1;
    });

    setSelectedStations(updatedStations);

    setRows((prevRows) =>
      prevRows.map((row) => {
        const updatedStation = updatedStations.find((s) => s.id === row.id);
        return updatedStation ? { ...row, order: updatedStation.order } : row;
      })
    );

    setDraggedStation(null);
    setHasUnsavedChanges(true);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError("");
    setSnackbarOpen(false);

    try {
      let finalImageUrl = announcementPhotoUrl;

      if (pendingImageFile) {
        try {
          const base64Data = await fileToBase64(pendingImageFile);
          const fileExtension = pendingImageFile.name
            .split(".")
            .pop()
            .toLowerCase();

          const uploadPayload = {
            action: "upload_image",
            communityId: communityId,
            imageData: base64Data,
            fileExtension: fileExtension,
          };

          let response;
          if (announcementPhotoUrl) {
            response = await updateKioskImage(uploadPayload);
          } else {
            response = await uploadKioskImage(uploadPayload);
          }

          if (response && response.imageUrl) {
            finalImageUrl = response.imageUrl;
          }
        } catch (uploadErr) {
          const uploadErrorMessage =
            uploadErr?.response?.data?.error ||
            uploadErr?.response?.data?.message ||
            uploadErr?.message ||
            "Failed to upload image";
          setError(uploadErrorMessage);
          setLoading(false);
          return;
        }
      }

      const selected = selectedStations.map(
        ({
          display,
          timer,
          default_latitude,
          default_longitude,
          order,
          location,
          lastTimer,
          status,
          languages,
          ...rest
        }) => ({
          ...rest,
          timer: safeToString(timer, DEFAULT_TIMER),
          order: order,
          languages: languages || { english: true, inuktitut: true },
        })
      );

      const mapConfigForDB = isValidMapConfig(mapConfig)
        ? {
            center: [
              safeToString(mapConfig.center[0], "0"),
              safeToString(mapConfig.center[1], "0"),
            ],
            zoom: safeToString(mapConfig.zoom, "5.2"),
            bounds: mapConfig.bounds
              ? [
                  [
                    safeToString(mapConfig.bounds[0][0], "0"),
                    safeToString(mapConfig.bounds[0][1], "0"),
                  ],
                  [
                    safeToString(mapConfig.bounds[1][0], "0"),
                    safeToString(mapConfig.bounds[1][1], "0"),
                  ],
                ]
              : null,
            languages: mapLanguages,
          }
        : null;

      const windBarbMapConfigForDB = showWindBarbMap
        ? {
            enabled: true,
            languages: windBarbMapLanguages,
          }
        : null;

      const payload = {
        communityId,
        communityName: community?.name || communityId,
        config: {
          stations: selected,
          map: mapConfigForDB,
          windBarbMap: windBarbMapConfigForDB,
        },
        imageUrl: finalImageUrl || "",
      };

      let response;
      if (configExists) {
        response = await patchKioskConfig(payload);
      } else {
        response = await postKioskConfig(payload);
        setConfigExists(true);
      }

      setPendingImageFile(null);

      const refreshedData = await getKioskConfig(communityId);

      if (
        refreshedData &&
        refreshedData.config &&
        Array.isArray(refreshedData.config.stations)
      ) {
        setRows((prevRows) =>
          prevRows.map((row) => {
            const found = refreshedData.config.stations.find(
              (s) => s.id === row.id
            );
            const currentStation = selectedStations.find(
              (s) => s.id === row.id
            );
            return found
              ? {
                  ...row,
                  display: true,
                  timer:
                    currentStation?.timer ||
                    found.timer?.toString() ||
                    DEFAULT_TIMER,
                  lastTimer:
                    currentStation?.timer ||
                    found.timer?.toString() ||
                    DEFAULT_TIMER,
                  order: found.order || 0,
                  languages: currentStation?.languages ||
                    found.languages || { english: true, inuktitut: true },
                }
              : {
                  ...row,
                  display: false,
                  timer: "",
                  lastTimer: DEFAULT_TIMER,
                  order: 0,
                  languages: { english: false, inuktitut: false },
                };
          })
        );

        const refreshedSelectedStations = refreshedData.config.stations
          .map((station, index) => {
            const currentStation = selectedStations.find(
              (s) => s.id === station.id
            );
            return {
              ...station,
              timer:
                currentStation?.timer ||
                station.timer?.toString() ||
                DEFAULT_TIMER,
              order: station.order || index + 1,
              languages: currentStation?.languages ||
                station.languages || { english: true, inuktitut: true },
            };
          })
          .sort((a, b) => a.order - b.order);
        setSelectedStations(refreshedSelectedStations);

        if (refreshedData.config.map) {
          const refreshedMapConfig = refreshedData.config.map;
          try {
            setMapConfig({
              center: refreshedMapConfig.center
                ? [
                    parseFloat(refreshedMapConfig.center[0]) || 0,
                    parseFloat(refreshedMapConfig.center[1]) || 0,
                  ]
                : null,
              zoom: refreshedMapConfig.zoom
                ? parseFloat(refreshedMapConfig.zoom) || 5.2
                : null,
              bounds: refreshedMapConfig.bounds
                ? [
                    [
                      parseFloat(refreshedMapConfig.bounds[0][0]) || 0,
                      parseFloat(refreshedMapConfig.bounds[0][1]) || 0,
                    ],
                    [
                      parseFloat(refreshedMapConfig.bounds[1][0]) || 0,
                      parseFloat(refreshedMapConfig.bounds[1][1]) || 0,
                    ],
                  ]
                : null,
            });

            // Refresh map languages
            if (refreshedMapConfig.languages) {
              setMapLanguages(refreshedMapConfig.languages);
            } else {
              setMapLanguages({ english: true, inuktitut: true });
            }
          } catch (error) {
            console.warn("Error parsing refreshed map configuration:", error);
            setMapConfig({
              center: null,
              zoom: null,
              bounds: null,
            });
            setMapLanguages({ english: true, inuktitut: true });
          }
        }

        if (refreshedData.imageUrl) {
          setAnnouncementPhotoUrl(refreshedData.imageUrl);
          setPendingImageFile(null);
        } else {
          setAnnouncementPhotoUrl("");
          setPendingImageFile(null);
        }

        if (refreshedData.introImages) {
          setIntroImages(refreshedData.introImages);
        } else {
          setIntroImages([]);
        }

        if (refreshedData.lastModified) {
          setLastModified(refreshedData.lastModified);
        }

        setSnackbarOpen(true);
        setNotConfigured(false);
        setHasUnsavedChanges(false);
      } else {
        throw new Error("Failed to refresh configuration data");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while saving the configuration";
      setError(errorMessage);
      setSnackbarOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const formatLastModified = (lastModified) => {
    if (!lastModified) return null;

    try {
      const date = new Date(lastModified + "Z");
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch (error) {
      console.warn("Error formatting lastModified date:", error);
      return null;
    }
  };

  const handlePreviewPhoto = () => {
    if (announcementPhotoUrl) {
      window.open(announcementPhotoUrl, "_blank", "noopener,noreferrer");
    }
  };

  const isValidImageFile = (file) => {
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!validTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return false;
    }

    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isValidImageFile(file)) {
      event.target.value = "";
      return;
    }

    setPendingImageFile(file);
    setError("");
    setHasUnsavedChanges(true);
  };

  const handleRemovePhoto = () => {
    setAnnouncementPhotoUrl("");
    setPendingImageFile(null);

    const fileInput = document.getElementById("announcement-photo-input");
    if (fileInput) {
      fileInput.value = "";
    }
    setHasUnsavedChanges(true);
  };

  return {
    // State
    community,
    rows,
    snackbarOpen,
    filters,
    search,
    loading,
    error,
    configExists,
    notConfigured,
    mapConfig,
    mapLanguages,
    showWindBarbMap,
    windBarbMapLanguages,
    mapDialogOpen,
    lastModified,
    selectedStations,
    draggedStation,
    announcementPhotoUrl,
    pendingImageFile,
    introImages,
    statusOptions,
    typeOptions,
    communityOptions,
    filteredRows,
    hasUnsavedChanges,

    // Handlers
    handleDisplayChange,
    handleLanguageChange,
    handleTimerChange,
    handleSearchChange,
    handleFilterChange,
    handleMapLanguageChange,
    handleWindBarbMapToggle,
    handleWindBarbMapLanguageChange,
    handleMapDialogOpen,
    handleMapDialogClose,
    handleMapAreaSelect,
    handleDeleteMap,
    moveStationUp,
    moveStationDown,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleSave,
    handleSnackbarClose,
    formatLastModified,
    handlePreviewPhoto,
    handleFileSelect,
    handleRemovePhoto,
  };
}
